import React, { useEffect, useRef, useState } from 'react'
import { gql, useMutation } from '@apollo/client'
import { useNavigate, useLocation } from 'react-router-dom'
import Cropper from 'react-easy-crop'
import 'react-easy-crop/react-easy-crop.css'
import './HomePage.css'

const apiBaseUrl = import.meta.env.VITE_API_BASE_URL

const UPDATE_PROFILE = gql`
  mutation UpdateProfile($input: UpdateProfileInput!) {
    updateProfile(input: $input) {
      id
      phone
      name
      email
      authProvider
      role
      createdAt
      avatarUrl
    }
  }
`

const REQUEST_OTP = gql`
  mutation RequestOtp($phone: String!) {
    requestOtp(phone: $phone) {
      success
      message
      otpDebug
    }
  }
`

const VERIFY_OTP = gql`
  mutation VerifyOtp($phone: String!, $otp: String!) {
    verifyOtp(phone: $phone, otp: $otp) {
      success
      message
      token
      user {
        id
        phone
        name
        email
        authProvider
        role
        createdAt
        avatarUrl
      }
    }
  }
`

const makeCroppedAvatar = (source, area) => new Promise((resolve, reject) => {
  const image = new Image()
  image.onload = () => {
    const canvas = document.createElement('canvas')
    canvas.width = 512
    canvas.height = 512
    const context = canvas.getContext('2d')
    if (!context) {
      reject(new Error('Unable to crop this image.'))
      return
    }
    context.drawImage(image, area.x, area.y, area.width, area.height, 0, 0, 512, 512)
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Unable to prepare this image.'))
        return
      }
      resolve(new File([blob], 'profile-avatar.webp', { type: 'image/webp' }))
    }, 'image/webp', 0.9)
  }
  image.onerror = () => reject(new Error('Unable to read this image.'))
  image.src = source
})

function ProfilePage({ user, onUserUpdate, onAuthSuccess }) {
  const navigate = useNavigate()
  const location = useLocation()
  const stateUser = location?.state?.user || null
  const initialUser = user || stateUser
  const redirectToken = location?.state?.authToken || null
  const redirectRemember = Boolean(location?.state?.remember)
  const [completionToken, setCompletionToken] = useState(redirectToken)
  const [name, setName] = useState(initialUser?.name || '')
  const [email, setEmail] = useState(initialUser?.email || '')
  const [phone, setPhone] = useState(initialUser?.phone || '')
  const [avatarUrl, setAvatarUrl] = useState(initialUser?.avatarUrl || '')
  const [avatarPreview, setAvatarPreview] = useState('')
  const [avatarFile, setAvatarFile] = useState(null)
  const [avatarLoading, setAvatarLoading] = useState(false)
  const [cropSource, setCropSource] = useState('')
  const [cropOpen, setCropOpen] = useState(false)
  const [photoActionsOpen, setPhotoActionsOpen] = useState(false)
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null)
  const [otp, setOtp] = useState('')
  const [otpRequested, setOtpRequested] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const avatarInput = useRef(null)

  useEffect(() => {
    if (initialUser?.avatarUrl) setAvatarUrl(initialUser.avatarUrl)
  }, [initialUser?.avatarUrl])

  useEffect(() => {
    if (!avatarPreview) return undefined
    return () => URL.revokeObjectURL(avatarPreview)
  }, [avatarPreview])

  useEffect(() => {
    if (!cropSource) return undefined
    return () => URL.revokeObjectURL(cropSource)
  }, [cropSource])

  useEffect(() => {
    if (redirectToken && typeof window !== 'undefined') {
      window.__PROFILE_TOKEN__ = redirectToken
    }
  }, [redirectToken])

  const [updateProfile, { loading: updateLoading }] = useMutation(UPDATE_PROFILE, {
    onCompleted: (data) => {
      const updatedUser = data?.updateProfile
      if (updatedUser) {
        onUserUpdate?.(updatedUser)

        if (completionToken && updatedUser.phone) {
          onAuthSuccess?.(updatedUser, completionToken, redirectRemember)
        }

        setSuccess('Profile updated successfully.')
        // If redirected here from social login, return to original route
        const returnTo = location?.state?.returnTo
        if (returnTo) {
          navigate(returnTo)
        }
      }
    },
    onError: (err) => setError(err.message)
  })

  const [requestOtp, { loading: requestLoading }] = useMutation(REQUEST_OTP, {
    onCompleted: (data) => {
      if (data?.requestOtp?.success) {
        setOtpRequested(true)
        setSuccess(data.requestOtp.otpDebug ? `Development OTP: ${data.requestOtp.otpDebug}` : data.requestOtp.message)
      }
    },
    onError: (err) => setError(err.message)
  })

  const [verifyOtp, { loading: verifyLoading }] = useMutation(VERIFY_OTP, {
    onCompleted: (data) => {
      if (!data?.verifyOtp?.success || !data.verifyOtp.token) {
        setError(data?.verifyOtp?.message || 'Phone verification failed.')
        return
      }

      setCompletionToken(data.verifyOtp.token)
      if (typeof window !== 'undefined') window.__PROFILE_TOKEN__ = data.verifyOtp.token

      updateProfile({
        variables: {
          input: {
            name: name.trim() || null,
            email: email.trim().toLowerCase() || null,
            phone: phone.replace(/\D/g, '').slice(0, 10)
          }
        }
      })
    },
    onError: (err) => setError(err.message)
  })

  const loading = updateLoading || requestLoading || verifyLoading || avatarLoading

  const handleAvatarSelection = (event) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return

    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setError('Choose a JPEG, PNG, or WebP image.')
      return
    }
    if (file.size > 4 * 1024 * 1024) {
      setError('Choose an image smaller than 4 MB.')
      return
    }

    setError('')
    setCropSource(URL.createObjectURL(file))
    setCrop({ x: 0, y: 0 })
    setZoom(1)
    setCropOpen(true)
  }

  const handleCropComplete = async () => {
    if (!cropSource || !croppedAreaPixels) return
    try {
      const croppedFile = await makeCroppedAvatar(cropSource, croppedAreaPixels)
      setAvatarFile(croppedFile)
      setAvatarPreview(URL.createObjectURL(croppedFile))
      setCropOpen(false)
      setCropSource('')
      await handleAvatarUpload(croppedFile)
    } catch (cropError) {
      setError(cropError.message || 'Unable to crop this image.')
    }
  }

  const handleAvatarUpload = async (file = avatarFile) => {
    if (!file) return
    setError('')
    setSuccess('')
    setAvatarLoading(true)

    try {
      const authToken = typeof window !== 'undefined' && (
        window.__SESSION_TOKEN__ ||
        window.__PROFILE_TOKEN__ ||
        sessionStorage.getItem('authToken') ||
        localStorage.getItem('token') ||
        localStorage.getItem('authToken')
      )
      if (!authToken) throw new Error('Please sign in again before changing your photo.')

      const response = await fetch(`${apiBaseUrl}/profile/avatar`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': file.type
        },
        body: file
      })
      const result = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(result.error || 'Unable to upload profile photo.')
      if (!result.avatarUrl) throw new Error('The image was saved, but no image URL was returned.')

      setAvatarUrl(result.avatarUrl)
      setAvatarFile(null)
      onUserUpdate?.({ avatarUrl: result.avatarUrl })
      setSuccess('Profile photo updated.')
    } catch (uploadError) {
      setError(uploadError.message || 'Unable to upload profile photo.')
    } finally {
      setAvatarLoading(false)
    }
  }

  const handleAvatarRemove = async () => {
    setError('')
    setSuccess('')
    setAvatarLoading(true)

    try {
      const authToken = typeof window !== 'undefined' && (
        window.__SESSION_TOKEN__ ||
        window.__PROFILE_TOKEN__ ||
        sessionStorage.getItem('authToken') ||
        localStorage.getItem('token') ||
        localStorage.getItem('authToken')
      )
      if (!authToken) throw new Error('Please sign in again before changing your photo.')

      const response = await fetch(`${apiBaseUrl}/profile/avatar`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${authToken}` }
      })
      const result = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(result.error || 'Unable to remove profile photo.')

      setAvatarUrl('')
      setAvatarPreview('')
      setAvatarFile(null)
      onUserUpdate?.({ avatarUrl: null })
      setSuccess('Profile photo removed.')
    } catch (removeError) {
      setError(removeError.message || 'Unable to remove profile photo.')
    } finally {
      setAvatarLoading(false)
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setError('')
    setSuccess('')

    const normalizedPhone = phone.replace(/\D/g, '').slice(0, 10)
    if (normalizedPhone.length !== 10) {
      setError('Please enter a valid 10-digit phone number.')
      return
    }

    if (!otpRequested) {
      requestOtp({ variables: { phone: normalizedPhone } })
      return
    }

    if (otp.length !== 6) {
      setError('Please enter the 6-digit OTP.')
      return
    }

    verifyOtp({ variables: { phone: normalizedPhone, otp } })
  }

  if (!initialUser) {
    return (
      <div className="container" style={{ padding: '3rem 0' }}>
        <h2>Profile</h2>
        <p>Please log in to view your profile.</p>
      </div>
    )
  }

  return (
    <div className="container" style={{ padding: '3rem 0' }}>
      <div className="auth-card-modern" style={{ maxWidth: '560px', margin: '0 auto' }}>
        <div className="auth-header">
          <h2>Update Profile</h2>
          <p>Keep your contact details up to date.</p>
        </div>

        <section className="profile-avatar-panel" aria-label="Profile photo">
          <div className="profile-avatar-frame">
            {avatarPreview || avatarUrl ? (
              <img
                src={avatarUrl || avatarPreview}
                alt="Profile"
                className="profile-avatar-image"
                onLoad={() => {
                  if (avatarUrl && avatarPreview) setAvatarPreview('')
                }}
                onError={() => {
                  if (avatarUrl) {
                    setAvatarUrl('')
                    onUserUpdate?.({ avatarUrl: null })
                  }
                }}
              />
            ) : (
              <span className="profile-avatar-initial" aria-hidden="true">
                {(name || email || 'M').trim().charAt(0).toUpperCase()}
              </span>
            )}
            <input
              ref={avatarInput}
              className="profile-avatar-input"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleAvatarSelection}
              disabled={loading}
            />
            <button
              type="button"
              className="profile-avatar-overlay profile-avatar-overlay-edit"
              onClick={() => {
                if (avatarUrl || avatarPreview) setPhotoActionsOpen(true)
                else avatarInput.current?.click()
              }}
              disabled={loading}
              aria-label="Choose profile photo"
              title="Choose profile photo"
            >
              <span className="material-icons" aria-hidden="true">edit</span>
            </button>
          </div>
          <div className="profile-avatar-actions">
            <strong>Profile photo</strong>
            <span>Square crop · JPEG, PNG, or WebP · up to 4 MB</span>
          </div>
        </section>

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label>Full Name</label>
            <input
              type="text"
              placeholder="Your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label>Email Address</label>
            <input
              type="email"
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label>Mobile Number { !initialUser?.phone && <span style={{ color: 'red' }}>*</span> }</label>
            <input
              type="tel"
              placeholder="10-digit mobile number"
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
              maxLength="10"
              disabled={loading || otpRequested}
              required
            />
          </div>

          {otpRequested && (
            <div className="form-group">
              <label>Phone OTP</label>
              <input
                type="text"
                placeholder="Enter 6-digit OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength="6"
                disabled={loading}
                required
              />
            </div>
          )}

          {error && <div className="error-message">{error}</div>}
          {success && <div className="info-message">{success}</div>}

          <button type="submit" className="btn-primary-modern" disabled={loading}>
            {loading ? 'Please wait...' : otpRequested ? 'Verify OTP and Save Profile' : 'Send Phone OTP'}
          </button>

          <button type="button" className="btn-secondary-modern" onClick={() => navigate('/products')} disabled={!initialUser?.phone}>
            Back to Recharge
          </button>
        </form>
      </div>
      {photoActionsOpen && (
        <div className="avatar-crop-backdrop" role="presentation" onMouseDown={(event) => {
          if (event.target === event.currentTarget) setPhotoActionsOpen(false)
        }}>
          <section className="avatar-actions-dialog" role="dialog" aria-modal="true" aria-labelledby="avatar-actions-title">
            <div className="avatar-crop-heading">
              <div>
                <h3 id="avatar-actions-title">Profile photo</h3>
                <p>Choose a new photo or remove the current one.</p>
              </div>
              <button
                type="button"
                className="profile-avatar-icon-button"
                onClick={() => setPhotoActionsOpen(false)}
                aria-label="Close photo actions"
                title="Close"
              >
                <span className="material-icons" aria-hidden="true">close</span>
              </button>
            </div>
            <div className="avatar-actions-list">
              <button
                type="button"
                className="avatar-action-row"
                onClick={() => {
                  setPhotoActionsOpen(false)
                  avatarInput.current?.click()
                }}
              >
                <span className="material-icons" aria-hidden="true">photo_camera</span>
                <span>Choose and crop a new photo</span>
              </button>
              {avatarFile && (
                <button
                  type="button"
                  className="avatar-action-row"
                  onClick={() => {
                    setPhotoActionsOpen(false)
                    handleAvatarUpload()
                  }}
                  disabled={loading}
                >
                  <span className="material-icons" aria-hidden="true">cloud_upload</span>
                  <span>Upload selected crop</span>
                </button>
              )}
              {avatarUrl && (
                <button
                  type="button"
                  className="avatar-action-row avatar-action-remove"
                  onClick={() => {
                    setPhotoActionsOpen(false)
                    handleAvatarRemove()
                  }}
                  disabled={loading}
                >
                  <span className="material-icons" aria-hidden="true">delete_outline</span>
                  <span>Remove current photo</span>
                </button>
              )}
              {avatarFile && !avatarUrl && (
                <button
                  type="button"
                  className="avatar-action-row avatar-action-remove"
                  onClick={() => {
                    setPhotoActionsOpen(false)
                    setAvatarFile(null)
                    setAvatarPreview('')
                  }}
                >
                  <span className="material-icons" aria-hidden="true">delete_outline</span>
                  <span>Discard selected crop</span>
                </button>
              )}
            </div>
          </section>
        </div>
      )}
      {cropOpen && (
        <div className="avatar-crop-backdrop" role="presentation" onMouseDown={(event) => {
          if (event.target === event.currentTarget) {
            setCropOpen(false)
            setCropSource('')
          }
        }}>
          <section className="avatar-crop-dialog" role="dialog" aria-modal="true" aria-labelledby="avatar-crop-title">
            <div className="avatar-crop-heading">
              <div>
                <h3 id="avatar-crop-title">Crop profile photo</h3>
                <p>Move and zoom to frame your photo.</p>
              </div>
              <button
                type="button"
                className="profile-avatar-icon-button"
                onClick={() => {
                  setCropOpen(false)
                  setCropSource('')
                }}
                aria-label="Cancel crop"
                title="Cancel crop"
              >
                <span className="material-icons" aria-hidden="true">close</span>
              </button>
            </div>
            <div className="avatar-crop-area">
              <Cropper
                image={cropSource}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={(_, pixels) => setCroppedAreaPixels(pixels)}
              />
            </div>
            <div className="avatar-crop-footer">
              <label className="avatar-crop-zoom">
                <span>Zoom</span>
                <input
                  type="range"
                  min="1"
                  max="3"
                  step="0.05"
                  value={zoom}
                  onChange={(event) => setZoom(Number(event.target.value))}
                  aria-label="Zoom crop"
                />
              </label>
              <button
                type="button"
                className="profile-avatar-icon-button profile-avatar-upload"
                onClick={handleCropComplete}
                disabled={!croppedAreaPixels}
                aria-label="Use this crop"
                title="Use this crop"
              >
                <span className="material-icons" aria-hidden="true">check</span>
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  )
}

export default ProfilePage
